import { useMemo, useState } from 'react';
import { FileText, BadgeCheck } from 'lucide-react';
import {
  useEntities,
  useEmployeesFull,
  useActivityLog,
  type ActivityLogEntryDTO,
} from '../api/hooks.js';

const thisMonth = () => new Date().toISOString().slice(0, 7);

function openUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function ReportsPage() {
  const entities = useEntities();
  const employees = useEmployeesFull();

  const [entityId, setEntityId] = useState('');
  const [month, setMonth] = useState(thisMonth());
  const [employeeId, setEmployeeId] = useState('');

  const effEntity = entityId || entities.data?.[0]?.id || '';
  const effEmp = employeeId || employees.data?.[0]?.id || '';

  return (
    <>
      <div className="page-header">
        <div className="page-eyebrow">Génération & exports</div>
        <h1 className="page-title">Rapports <em>& fiches de salaire</em></h1>
        <p className="page-subtitle">
          Générez les fiches individuelles et les attestations de travail (à l'unité ou en lot ZIP).
          L'en-tête et les mentions légales viennent de Paramètres → Sociétés.
        </p>
      </div>

      {/* Shared pickers */}
      <div className="toolbar" style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 12.5, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Entité
          <select className="set-input" value={effEntity} onChange={(e) => setEntityId(e.target.value)} data-testid="rep-entity" aria-label="Entité">
            {(entities.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12.5, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Mois
          <input className="set-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="rep-month" aria-label="Mois" />
        </label>
        <label style={{ fontSize: 12.5, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Employé
          <select className="set-input" value={effEmp} onChange={(e) => setEmployeeId(e.target.value)} data-testid="rep-employee" aria-label="Employé">
            {(employees.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
      </div>

      {/* Deux documents : la fiche individuelle et l'attestation. La
          grille mensuelle se génère depuis son propre écran (Grille
          mensuelle), et le journal des relances depuis Activité. */}
      <div className="report-tiles">
        <Tile
          tint="var(--oct-soft)" fg="var(--oct)" Icon={FileText}
          title="Fiche de salaire"
          desc="Fiche individuelle : heures, retards, soldes congé/récup à la date."
        >
          <button className="btn btn-sm btn-primary" data-testid="rep-fiche-gen"
            disabled={!effEmp}
            onClick={() => openUrl(`/api/reports/employee/${effEmp}/pdf?month=${month}`)}>
            Générer
          </button>
        </Tile>

        <Tile
          tint="var(--sgn-soft)" fg="var(--sgn)" Icon={BadgeCheck}
          title="Attestation de travail"
          desc="Génération auto par employé, en-tête et mentions légales de la société. PDF signé."
        >
          <button className="btn btn-sm btn-primary" data-testid="rep-attest-one"
            disabled={!effEmp}
            onClick={() => openUrl(`/api/employees/${effEmp}/attestation`)}>
            Générer
          </button>
          <button className="btn btn-sm btn-ghost" data-testid="rep-attest-zip"
            onClick={() => openUrl(`/api/reports/attestations.zip${effEntity ? `?entityId=${effEntity}` : ''}`)}>
            En lot (ZIP)
          </button>
        </Tile>
      </div>

      <RecentReports />
    </>
  );
}

function Tile({
  tint, fg, Icon, title, desc, children,
}: {
  tint: string; fg: string; Icon: typeof FileText; title: string; desc: string; children: React.ReactNode;
}) {
  return (
    <div className="report-tile" style={{ ['--tile-tint' as string]: tint }} data-testid={`tile-${title}`}>
      <div className="report-tile-icon" style={{ background: tint, color: fg }}>
        <Icon size={20} aria-hidden="true" />
      </div>
      <div className="report-tile-title">{title}</div>
      <div className="report-tile-desc">{desc}</div>
      <div className="report-tile-actions">{children}</div>
    </div>
  );
}

const REPORT_LABELS: Record<string, string> = {
  'reports.pdf.generated': 'Rapport PDF',
  'reports.zip.generated': 'Attestations (ZIP)',
};

function describeTarget(p: Record<string, unknown> | null): string {
  if (!p) return '—';
  if (p.kind === 'monthly') return `Grille mensuelle · ${String(p.month ?? '')}`;
  if (p.kind === 'employee') return `Fiche · ${String(p.month ?? `${p.from}→${p.to}`)}`;
  if (p.kind === 'attestations') return `Attestations · ${p.count ?? ''} employés`;
  return String(p.kind ?? '—');
}

function RecentReports() {
  // Pull a recent slice of the audit log and keep only report-generation rows.
  const log = useActivityLog({ limit: 200 });
  const reports = useMemo<ActivityLogEntryDTO[]>(
    () => (log.data?.entries ?? []).filter((e) => e.action.startsWith('reports.')).slice(0, 20),
    [log.data],
  );

  return (
    <div className="card" data-testid="recent-reports">
      <div className="card-header">
        <div className="card-title">Rapports récents <span className="card-title-count">{reports.length}</span></div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: '22%' }}>Type</th>
            <th>Cible</th>
            <th style={{ width: '20%' }}>Généré par</th>
            <th style={{ width: '20%' }}>Date</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id} data-testid={`report-${r.id}`}>
              <td><strong>{REPORT_LABELS[r.action] ?? r.action}</strong></td>
              <td>{describeTarget(r.payload)}</td>
              <td>{r.user?.name ?? '—'}</td>
              <td className="num muted">{r.createdAt.replace('T', ' ').slice(0, 16)}</td>
            </tr>
          ))}
          {reports.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 20 }}>Aucun rapport généré récemment.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
